import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { reactNavHrefForRouteHref } from '@/lib/react-admin-route-map';
import type { PermissionCatalogEntry } from '@/lib/types';
import { cn } from '@/lib/utils';

function normalizeHref(href: string) {
  return href.replace(/^\/+/, '');
}

export function isAdminNavEntryActive(entry: PermissionCatalogEntry, activeRoute: string) {
  return normalizeHref(entry.href) === normalizeHref(activeRoute);
}

interface AdminNavItemProps {
  entry: PermissionCatalogEntry;
  activeRoute?: string;
  variant: 'bar' | 'menu';
  /** Role editor draft nav preview: same styling, no navigation, strikethrough when page access is off. */
  preview?: { pageAccessGranted: boolean };
}

export function AdminNavItem({ entry, activeRoute = '', variant, preview }: AdminNavItemProps) {
  const routeHref = normalizeHref(entry.href);
  const isActive = isAdminNavEntryActive(entry, activeRoute);
  const href = reactNavHrefForRouteHref(routeHref);
  const deniedClass =
    preview && !preview.pageAccessGranted
      ? 'line-through decoration-2 decoration-foreground/70'
      : undefined;

  if (preview) {
    const stateLabel = preview.pageAccessGranted ? 'access granted' : 'access not granted';
    const a11y = { 'aria-label': `${entry.label}, ${stateLabel}`, title: `${entry.label}: ${stateLabel}` };

    if (variant === 'menu') {
      return (
        <DropdownMenuItem
          {...a11y}
          data-nav={entry.id}
          render={
            <span
              className={cn('block w-full min-w-0 text-foreground', deniedClass)}
              onClick={(event) => event.preventDefault()}
            />
          }
        >
          {entry.label}
        </DropdownMenuItem>
      );
    }

    return (
      <Button
        {...a11y}
        type="button"
        data-nav={entry.id}
        variant="ghost"
        size="sm"
        tabIndex={-1}
        className={cn('shrink-0 cursor-default', deniedClass)}
        onClick={(event) => event.preventDefault()}
      >
        {entry.label}
      </Button>
    );
  }

  if (variant === 'menu') {
    return (
      <DropdownMenuItem
        render={
          <a
            aria-current={isActive ? 'page' : undefined}
            className={cn(isActive && 'brand-nav-active font-semibold', !isActive && 'text-foreground')}
            href={href}
          />
        }
      >
        {entry.label}
      </DropdownMenuItem>
    );
  }

  return (
    <Button
      render={<a href={href} aria-current={isActive ? 'page' : undefined} />}
      variant="ghost"
      size="sm"
      className={cn(isActive && 'brand-nav-active font-semibold hover:bg-[var(--brand-primary-soft)]')}
    >
      {entry.label}
    </Button>
  );
}
